<template>
    <form class="settings-form">
        <div v-show="activeTab === 'general'" class="general-tab">
            <div class="general-settings mb-3">
                <form ref="settingsForm" class="basic-settings" @submit.stop.prevent="save">
                    <b-form-group class="email" :label="$translate('contact_email')">
                        <b-form-input v-model="userModel.contactEmail" type="email" />
                    </b-form-group>

                    <b-form-group
                        class="clinic-name"
                        :label="$translate('clinic_name')"
                        :invalid-feedback="$translate('clinic_name_cant_be_empty')"
                    >
                        <b-form-input v-model="tenantModel.clinicName" type="text" />
                    </b-form-group>

                    <b-form-group
                        class="city"
                        :label="$translate('city')"
                        :invalid-feedback="$translate('city_name_cant_be_empty')"
                    >
                        <b-form-input v-model="tenantModel.cityName" type="text" />
                    </b-form-group>
                </form>
            </div>
            <span class="">{{ $translate('security') }}</span>

            <div class="h-divider" />

            <div class="pb-2 mb-3">
                <div class="mt-4 mb-1 justify-between">
                    <b-form-group :label="$translate('login_email')">
                        <span class="">&nbsp;&nbsp;&nbsp;&nbsp;{{ user.email }}</span>
                    </b-form-group>

                    <div class="pt-4">
                        <b-button class="btn btn-secondary" @click="changePassword">
                            {{ $translate('change_password') }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import 'password-strength-meter'
import 'vue-croppa/dist/vue-croppa.css'
import UIUtils from '../../../business/utils/UIUtils'
import BackendProvider from '../../../business/services/BackendProvider'

const TABS = ['general', 'share_with_patient']

export default {
    name: 'Settings',
    data: () => ({
        TABS,
        activeTab: 'general',
        userModel: {
            contactEmail: '',
            selectedTenant: null
        },
        tenantModel: {
            clinicName: '',
            cityName: ''
        },
        reloadRequired: false
    }),
    computed: {
        ...mapState({
            user: state => state.common.user,
            locale: state => state.locale.locale,
            locales: state => state.locale.locales
        }),
        ...mapGetters({
            tenant: 'common/getTenant',
            translationByLang: 'locale/translationByLang'
        })
    },
    mounted() {
        this.setDefaults()
    },
    methods: {
        setDefaults() {
            this.userModel.selectedTenant = this.user.tenants[0]
            this.userModel.contactEmail = this.user.contactEmail || this.user.email
            this.tenantModel.clinicName = this.tenant.clinicName
            this.tenantModel.cityName = this.tenant.city
        },
        async save() {
            if (this.$refs.settingsForm && this.$refs.settingsForm.checkValidity()) {
                await this.sendRequest()
            }
        },
        async sendRequest() {
            try {
                UIUtils.showLoadingMask()

                this.reloadRequired = false

                await this.saveUserProfile()
                await this.saveTenantSettings()

                if (this.reloadRequired) {
                    await this.$store.dispatch('common/setUserData')
                    this.$store.commit('common/setSelectedTenant', this.user.tenants[0])
                }
                this.$emit('ok')
            } catch (e) {
                console.error(e)

                if (e.body && e.body.message) {
                    UIUtils.toast(e.body.message.toString(), 'error')
                } else if (e.body && e.body.error) {
                    UIUtils.toast(e.body.error.toString(), 'error')
                } else if (e.message) {
                    UIUtils.toast(e.message.toString(), 'error')
                } else {
                    UIUtils.toast(e.statusText.toString(), 'error')
                }
                return false
            } finally {
                UIUtils.hideLoadingMask()
            }
        },
        async saveUserProfile() {
            this.$log.actionEvent('change_user_profile_settings')

            const { contactEmail } = this.userModel

            if (contactEmail !== (this.user.contactEmail || this.user.email)) {
                await BackendProvider.updateUserProfile({
                    contactEmail: contactEmail
                })
                this.reloadRequired = true
            }
        },
        async saveTenantSettings() {
            const { clinicName, cityName } = this.tenantModel
            const body = {
                settings: {}
            }
            // tenant properties
            if (cityName !== this.tenant.city) {
                body.city = cityName
            }
            if (clinicName !== this.tenant.clinicName) {
                body.clinicName = clinicName
            }
            if (Object.keys(body.settings).length === 0) {
                delete body.settings
            }

            if (Object.keys(body).length) {
                await BackendProvider.updateTenantSettings(body, this.tenant._id)
                this.reloadRequired = true
            }
        },
        changePassword() {
            UIUtils.toast(this.$translate('password_reset_instructions'))
        }
    }
}
</script>

<style lang="scss">
.settings-form {
    .tabs {
        width: 100%;
        margin-bottom: 1.5rem;
        padding-bottom: 0.5rem;
        border-bottom: $border-narrow-dark;

        .tab {
            &:not(:last-child) {
                margin-right: 1rem;
            }

            &:hover,
            &.selected {
                color: $primary;
            }

            &:not(.selected) {
                cursor: pointer;
            }
        }
    }
    .general-tab {
        .general-settings {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            column-gap: 20px;
            justify-items: end;
            justify-content: space-between;

            .basic-settings {
                width: 100%;
                display: grid;
                column-gap: 20px;
                align-items: end;
                grid-template-columns: repeat(2, 1fr);
                grid-template-areas:
                    'email      email'
                    'clinicname city';
                .email {
                    grid-area: email;
                }
                .clinic-name {
                    grid-area: clinicname;
                }
                .city {
                    grid-area: city;
                }
            }
        }
    }
}
</style>
